
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { toInteger } from "lodash";

//Components
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Select,
    ProgressBar,
} from "@gpstrackit/react-controls";
import { Alert } from '@mui/material';

//Notification
import { NotificationManager } from "Components/Notifications";

//Helpers
import { groupByHardwareVersion } from 'Core/data/Helpers';

//Actions
import {
    assignFirmwareSearchCamerasByAccountRequest,
    assignFirmwareGetDeviceRequest,
    assignFirmwareSearchRequest,
    bulkAssignFirmwareRequest,
    assignFirmwareSetDevices,
    clearRedux,
} from 'Redux/actions';

//Views
import StepThree from './.components/stepThree';
import StepOne from './.components/stepOne';
import StepTwo from './.components/stepTwo';

//Permission
import { HasPermission } from "Core/security";

//Styles
import './styles.scss'

const AssignFirmware = (props) => {
    const { single, account } = props;

    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Local States
    const [entity, setEntity] = useState({ id: "entities", name: messages['assign_firmware_devices'] });
    const [firmware, setFirmware] = useState(null);
    const [error, setError] = useState(null);
    const [serial, setSerial] = useState("");
    const [step, setStep] = useState(1);

    //Redux States
    const {
        loadingBulkUpdateFirmware,
        groupingByHarwareVersion,
        groupingSelectedCameras,
        bulkUpdateFirmware,
        loadingAllCameras,
        backupAllCameras,
        loadingFirmwares,
        selectedCameras,
        allCameras,
        firmwares,
        openModal,
        loading,
        result,
        device
    } = useSelector(state => state.assignFirmwareDeviceRedux);
    const user = useSelector(state => state.securityRedux.user);
    const closeModal = () => {
        dispatch(clearRedux("ASSIGN_FIRMWARE"));
        setSerial("");
        setStep(1);
        setFirmware(null);
        setError(null);
        setEntity({
            id: "entities",
            name: messages['assign_firmware_devices']
        })
    }

    const applyBulk = () => {
        let current
        let companyId = null
        switch (entity.id) {
            case "hardwareVersion":
                current = [...groupingByHarwareVersion];
                companyId = account?.id;
                break;
            case "all":
                current = [...allCameras];
                companyId = account?.id;
                break;
            case "entities":
                current = [...groupingSelectedCameras];
                break;
            default:
                return;
        }

        let payload = current.map((item) => {
            const validUnitIds = item?.items?.map((subItem) => subItem?.id).filter((id) => typeof id === "number" && !isNaN(id));
            return ({
                firmwareId: item?.newFirmware?.firmwareId,
                hardwareVersion: item?.hardwareVersion,
                scopes: entity.id !== 'entities' ? ["Company"] : ["Partial"],
                unitIds: entity.id !== 'entities' ? [] : validUnitIds,
                companyId: companyId
            })
        })
        dispatch(bulkAssignFirmwareRequest(payload));
    }

    const getDevice = () => {
        let validateSerial = selectedCameras && selectedCameras.some((item) => item?.esn === serial);
        if (!validateSerial) {
            dispatch(assignFirmwareGetDeviceRequest(serial?.trim()))
        } else {
            setSerial("");
            NotificationManager.warning(
                messages['assign_firmware_the_device_is_already_added'],
                messages['assign_firmware_title'],
                5000,
            );
        }
    }

    const applyFirmware = () => {
        let devices = selectedCameras.map((camera) => (camera.id))
        let validateHardwareVersion = selectedCameras.find((camera) => camera.id)
        let currentHardwareVersion = validateHardwareVersion?.unitConfig?.hardwareVersion;
        let data = [{
            firmwareId: +firmware?.firmwareId,
            hardwareVersion: currentHardwareVersion,
            unitIds: devices,
            scopes: ["Partial"],

        }]
        dispatch(bulkAssignFirmwareRequest(data))
    }

    const firmwareHandler = (value) => {
        selectedCameras.map((item) => {
            if (item?.unitConfig?.firmwareId) {
                let selected = firmwares.find(x => x.version === item?.unitConfig?.firmwareId)
                if (selected) {
                    if (_.parseInt(selected.firmwareId) > _.parseInt(value.firmwareId)) {
                        setError(messages['assign_firmware_error_firmware_currently'])
                    } else if (_.parseInt(selected.firmwareId) === _.parseInt(value.firmwareId)) {
                        setError(messages['assign_firmware_error_firmware_different'])
                    } else {
                        setError(null);
                    }
                } else {
                    setError(null);
                }
            }
        })
        setFirmware(value)
    }

    const compareFirmware = (first, second) => {
        let isBigger = 0;

        if (first?.unitConfig?.firmwareId !== "" && second?.unitConfig?.firmwareId === "")
            return -1;
        else if (first?.unitConfig?.firmwareId === "" && second?.unitConfig?.firmwareId !== "")
            return 1
        else if (first?.unitConfig?.firmwareId === "" && second?.unitConfig?.firmwareId === "")
            return 0;

        const firstFirmwareId = firmwares.find(x => x.version === first?.unitConfig?.firmwareId)?.firmwareId || 0;
        const secondFirmwareId = firmwares.find(x => x.version === second?.unitConfig?.firmwareId)?.firmwareId || 0;


        if (firstFirmwareId !== secondFirmwareId) {
            if (firstFirmwareId > secondFirmwareId) {
                isBigger = -1;
            } else if (firstFirmwareId < secondFirmwareId) {
                isBigger = 1;
            }

        }

        return isBigger;
    }

    const processFirmwareData = (selectedCameras, firmwares, user, hasPermissionCallback) => {

        if (single) {
            // Clonar y ordenar dispositivos
            let devices = [...selectedCameras];
            devices.sort(compareFirmware);

            // Ordenar firmwares por firmwareId
            let firmwaresSorted = [...firmwares];
            firmwaresSorted.sort((a, b) => {
                let ida = toInteger(a.firmwareId);
                let idb = toInteger(b.firmwareId);
                if (ida > idb) return -1;
                else if (ida < idb) return 1;
                return 0;
            });

            // Filtrar firmwares activos
            let fws = firmwares?.filter(x => x.isActive);
            let fwDefault = firmwares?.find(x => x.isActive && x.isDefault);
            let newfws = [];
            if (devices[0]?.unitConfig?.firmwareId !== "") {
                let currentVersion = firmwares?.find(x => x.version === devices[0]?.unitConfig?.firmwareId)
                if (currentVersion) {
                    //
                    if (currentVersion.firmwareId < fwDefault?.firmwareId) {
                        fws = [fwDefault]
                    }

                    //new Firmwares
                    newfws = firmwares.filter(x =>
                        toInteger(x?.firmwareId) > toInteger(currentVersion.firmwareId) &&
                        toInteger(x?.firmwareId) > toInteger(fwDefault?.firmwareId) && x.isActive
                    );
                } else {
                    fws = [fwDefault]
                    //new Firmwares
                    newfws = firmwares.filter(x =>
                        toInteger(x?.firmwareId) > toInteger(fwDefault?.firmwareId) && x.isActive
                    );
                }

            }

            // Verificar permisos y combinar firmwares
            if (hasPermissionCallback(user, "aslf")) {

                fws = [...newfws, ...fws];
            }

            // permissions to apply beta firmwares
            if (!HasPermission(user, "asbtf")) {
                fws = fws.filter(x => x?.build !== "BETA");
            }

            return {
                devices,
                firmwaresSorted,
                filteredFirmwares: fws
            };
        } else {
            return {}
        }
    }

    const entityHandler = (newEntity) => {
        if (account?.id && !allCameras.length) {
            dispatch(assignFirmwareSearchCamerasByAccountRequest({ companyId: account?.id, allFirmwares: HasPermission(user, "aslf") }))
        }
        setEntity(newEntity)
    }

    useEffect(() => {
        if (device && !device.error) {
            let validateVidfleet = device?.deviceModel?.manufacturer?.name !== "VidFleet";
            if (!validateVidfleet) {
                let currentSelectedCameras = [...selectedCameras] || []
                currentSelectedCameras.push(device);
                dispatch(assignFirmwareSetDevices(currentSelectedCameras));
                setSerial("");
                dispatch(clearRedux("ASSIGN_FIRMWARE", { device: null }))
            } else {
                NotificationManager.warning(
                    messages['assign_firmware_the_manufacturer_of_the_device_must_be_vidfleet'],
                    messages['assign_firmware_title'],
                    5000,
                );
                dispatch(clearRedux("ASSIGN_FIRMWARE", { device: null }))
            }
        } else if (device?.error) {
            setSerial("");
            NotificationManager.warning(
                messages['assign_firmware_no_device_found'],
                messages['assign_firmware_title'],
                5000,
            );
            dispatch(clearRedux("ASSIGN_FIRMWARE", { device: null }))
        }
        if (result) {
            if (result?.upgrade) {
                NotificationManager.success(
                    messages['assign_firmware_the_firmware_has_been_updated'],
                    messages['assign_firmware_title'],
                    5000,
                );
                closeModal();
            }
            else {
                NotificationManager.warning(
                    messages['assign_firmware_firmware_upgrade_not_posible'],
                    messages['assign_firmware_title'],
                    5000,
                );
            }
            dispatch(clearRedux("ASSIGN_FIRMWARE", { result: null }))
        }
    }, [device, result])

    useEffect(() => {
        if (openModal && selectedCameras && selectedCameras.length > 0 && single) {
            let currentHardwareVersion = selectedCameras.find((item) => item?.unitConfig?.hardwareVersion)
            if (currentHardwareVersion) {
                dispatch(assignFirmwareSearchRequest([currentHardwareVersion?.unitConfig?.hardwareVersion]))
            }
        }
    }, [openModal, selectedCameras])

    useEffect(() => {
        if (firmwares && !single && selectedCameras.length) {
            const allFirmwares = HasPermission(user, "aslf")
            const betaFirmwares = HasPermission(user, "asbtf")
            let groups = groupByHardwareVersion(selectedCameras, firmwares, allFirmwares, betaFirmwares);
            dispatch(clearRedux("ASSIGN_FIRMWARE", { groupingSelectedCameras: groups }));
        }
    }, [firmwares])

    useEffect(() => {
        if (bulkUpdateFirmware) {
            if (bulkUpdateFirmware?.update) {
                NotificationManager.success(
                    messages['assign_firmware_the_firmware_has_been_updated'],
                    messages['assign_firmware_title'],
                    5000,
                );
                closeModal();
            }
            else {
                NotificationManager.warning(
                    messages['assign_firmware_firmware_upgrade_not_posible'],
                    messages['assign_firmware_title'],
                    5000,
                );
            }
            dispatch(clearRedux("ASSIGN_FIRMWARE", { bulkUpdateFirmware: null }))
        }
    }, [bulkUpdateFirmware])

    useEffect(() => {
        if (step === 1 && account?.id && entity.id === "all" && backupAllCameras.length) {
            let currentBackupAllCameras = backupAllCameras.map((item) => ({ ...item, newFirmware: null }))
            dispatch(clearRedux("ASSIGN_FIRMWARE", { allCameras: [...currentBackupAllCameras] }))
        }
    }, [step])


    const options = processFirmwareData(selectedCameras, firmwares, user, HasPermission);

    return (
        <Modal
            open={openModal}
            title={`${messages['assign_firmware_title']} ${account?.name ? `(${account?.name})` : ""}`}
            handleClose={() => closeModal()}
            position="right"
            size="small"
            loading={loading || loadingFirmwares || loadingAllCameras || loadingBulkUpdateFirmware}
        >
            <ModalBody>
                <div style={{ padding: '0 10px', marginBottom: 30 }}>
                    {
                        !single ? (
                            <>
                                <div className="assing-firmware-progressbar-container">
                                    <ProgressBar steps={4} activeStep={single ? 4 : step} />
                                </div>
                                {step === 1 && (
                                    <StepOne
                                        serial={serial}
                                        setSerial={setSerial}
                                        entity={entity}
                                        entityHandler={entityHandler}
                                        selectedCameras={selectedCameras}
                                        getDevice={getDevice}
                                        account={account}
                                    />
                                )}
                                {step === 2 && <StepTwo entity={entity} assignLastFirmwares={HasPermission(user, "aslf")} assignBetaFirmwares={HasPermission(user, "asbtf")} firmwares={firmwares} />}
                                {step === 3 && <StepThree entity={entity} />}
                            </>
                        ) : (
                            <>
                                <Select
                                    id="firmware-list"
                                    label={messages['assign_firmware_label']}
                                    value={firmware}
                                    onChange={(value) => firmwareHandler(value)}
                                    placeholder={messages['assign_firmware_label']}
                                    options={options?.filteredFirmwares || []}
                                    valueField="version"
                                    textField="label"
                                    required
                                    requiredMessage={messages['required_field']}
                                    loading={loadingFirmwares}
                                />
                                {
                                    error &&
                                    <Alert severity="warning" style={{ marginTop: 10 }}>
                                        {error}
                                    </Alert>
                                }
                            </>
                        )
                    }
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-cancel"
                    onClick={() => {
                        if (step === 2) {
                            let current, key;
                            switch (entity.id) {
                                case "hardwareVersion":
                                    current = [...groupingByHarwareVersion];
                                    key = "groupingByHarwareVersion";
                                    break;
                                case "all":
                                    current = [...allCameras];
                                    key = "allCameras";
                                    break;
                                case "entities":
                                    current = [...groupingSelectedCameras];
                                    key = "groupingSelectedCameras";
                                    break;
                                default:
                                    return;
                            }
                            if (current) {
                                current = current.map((item) => ({ ...item, newFirmware: null }));
                                dispatch(clearRedux("ASSIGN_FIRMWARE", { [key]: current }));
                            }
                        }

                        if (step === 1 || single) closeModal()
                        else setStep(step - 1)
                    }}
                >
                    {
                        (step === 1 || single) ? messages['common_cancel'] : messages['common_back']
                    }
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => {
                        if (single) {
                            applyFirmware();
                        } else {
                            let validEntities = ["entities"]
                            if (validEntities.includes(entity?.id) && step === 1) {
                                const allFirmwares = HasPermission(user, "aslf")
                                const betaFirmwares = HasPermission(user, "asbtf")
                                let groups = groupByHardwareVersion(selectedCameras, null, null, allFirmwares, betaFirmwares);
                                let hardwareVersions = groups.map((item) => item?.hardwareVersion);
                                dispatch(assignFirmwareSearchRequest(hardwareVersions));
                            }
                            if (step === 3) applyBulk()
                            else setStep(step + 1)
                        }
                    }}
                    disabled={
                        (step === 1 && entity?.id === "entities" && !selectedCameras?.length) ||
                        (step === 1 && entity?.id === "hardwareVersion" && !groupingByHarwareVersion?.length) ||
                        (step === 2 && entity?.id === "all" && allCameras.some((item) => !item?.newFirmware) || step === 2 && entity?.id === "all" && !allCameras?.length) ||
                        (step === 2 && entity?.id === "hardwareVersion" && groupingByHarwareVersion?.some((item) => !item?.newFirmware) || step === 2 && entity?.id === "hardwareVersion" && !groupingByHarwareVersion?.length) ||
                        (step === 2 && entity?.id === "entities" && groupingSelectedCameras?.some((item) => !item?.newFirmware) || step === 2 && entity?.id === "entities" && !groupingSelectedCameras?.length) ||
                        (single && !firmware)
                    }
                >
                    {(step === 3 || single) ? messages['common_save'] : messages['common_next']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default AssignFirmware;